'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { LeaderboardModal } from '@/features/lobby/leaderboard/LeaderboardModal';

export type LeaderboardModalContextValue = {
  openLeaderboard: () => void;
  closeLeaderboard: () => void;
  /** True while the merged leaderboard dialog is open. */
  isOpen: boolean;
};

const LeaderboardModalContext = createContext<LeaderboardModalContextValue | null>(null);

export function LeaderboardModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openLeaderboard = useCallback(() => setOpen(true), []);
  const closeLeaderboard = useCallback(() => setOpen(false), []);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // The retired `/leaderboard` page now redirects to `/?lb=1` (see
  // `middleware.ts`). When the lobby loads with that query we pop the
  // modal automatically and clear the param so refresh / back-nav don't
  // re-open it.
  useEffect(() => {
    if (!searchParams) return;
    if (searchParams.get('lb') !== '1') return;
    setOpen(true);
    const next = new URLSearchParams(searchParams.toString());
    next.delete('lb');
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  const value = useMemo(
    () => ({
      openLeaderboard,
      closeLeaderboard,
      isOpen: open,
    }),
    [open, openLeaderboard, closeLeaderboard],
  );

  return (
    <LeaderboardModalContext.Provider value={value}>
      {children}
      <LeaderboardModal open={open} onOpenChange={setOpen} />
    </LeaderboardModalContext.Provider>
  );
}

export function useLeaderboardModal(): LeaderboardModalContextValue {
  const ctx = useContext(LeaderboardModalContext);
  if (!ctx) {
    throw new Error('useLeaderboardModal must be used within LeaderboardModalProvider');
  }
  return ctx;
}

export function useLeaderboardModalOptional(): LeaderboardModalContextValue | null {
  return useContext(LeaderboardModalContext);
}
